import { Link } from '@tanstack/react-router';
import { CompactID, type CompactIDProps } from '@interxfi/design-system/components/format/id/compact';
import type { CellContext } from '@interxfi/design-system/components/table';
import { useTableRowContext } from '@interxfi/design-system/components/table/row-context';

export function useDefaultCompactIDCellProps(): Partial<CompactIDProps> {
  const { rowLevelInteraction } = useTableRowContext();

  switch (rowLevelInteraction?.type) {
    case 'link':
      return {
        LinkElement: rowLevelInteraction.link.LinkElement || Link,
        to: rowLevelInteraction.link.to,
        showClickable: false,
      };
    default:
      return {};
  }
}

export function DefaultCompactIDCell<TData, TValue>({ getValue }: CellContext<TData, TValue>) {
  const idProps = useDefaultCompactIDCellProps();
  const value = getValue<string | undefined>();
  if (!value) {
    return null;
  }

  return <CompactID id={value} {...idProps} />;
}

export function getLinkedCompactIDCell<TData, TValue>(
  linkBuilder: (row: TData, accessorValue: string | undefined) => string | undefined,
  extraProps?: (row: TData) => Partial<CompactIDProps>,
) {
  return function LinkedCompactIDCell({ getValue, row }: CellContext<TData, TValue>) {
    const value = getValue<string | undefined>();
    const linkTo = row.original ? linkBuilder(row.original, value) : undefined;
    const idProps = row.original ? extraProps?.(row.original) || {} : {};
    if (!value) {
      return null;
    }

    return <CompactID {...idProps} id={value} LinkElement={Link} to={linkTo} />;
  };
}
